/**
 * RAS — Retail Audit Support.
 *
 * Internal audit report generator. Role-gated: Compliance + Auditor +
 * SuperAdmin only. /v3/audit-reports/* routes are PLANNED — the scaffold
 * renders friendly "coming soon" states where routes aren't live.
 */

import { useState } from 'react';
import { Link, Route, Routes, useNavigate } from 'react-router';
import { FileSearch, History, Shuffle } from '@retail-ops/panel-shell/fi';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@retail-ops/shared-types/enums';
import { Pill } from '@/components/primitives';
import { TemplatePicker } from './components/TemplatePicker';
import { ReportRunner } from './ReportRunner';
import { ReportHistory } from './ReportHistory';
import { AicpaSampling } from './AicpaSampling';

const ALLOWED_ROLES: UserRole[] = ['SuperAdmin'];

function Gate({ children }: { children: JSX.Element }): JSX.Element {
  const { user } = useAuth();
  if (!user || !ALLOWED_ROLES.includes(user.primaryRole)) {
    return (
      <div
        style={{
          padding: 'var(--sp-10)',
          background: 'var(--err-bg)',
          color: 'var(--err-text)',
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--pcb-red)',
        }}
      >
        <strong>Not authorized.</strong> RAS is limited to SuperAdmin (Risk reviewers use the
        risk-mgmt-panel). Current role: {user?.primaryRole ?? 'unknown'}.
      </div>
    );
  }
  return children;
}

function Dashboard(): JSX.Element {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | undefined>();

  return (
    <div>
      <header
        style={{
          marginBottom: 'var(--sp-6)',
          paddingBottom: 'var(--sp-5)',
          borderBottom: '1px solid var(--surface-border)',
        }}
      >
        <h1 style={{ margin: 0 }}>Retail Audit Support</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-600)' }}>
          Internal audit report generator — 14 pre-built templates + ad-hoc query builder. Outputs
          PDF / CSV / XML (NAUPA). Delivery: Teams DM or Secure Portal. Email delivery is explicitly
          forbidden.
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-7)',
          flexWrap: 'wrap',
        }}
      >
        <Link to="/ras/history" style={linkChipStyle}>
          <History size={14} /> Report history
        </Link>
        <Link to="/ras/sampling" style={linkChipStyle}>
          <Shuffle size={14} /> AICPA sampling helper
        </Link>
        <Pill variant="info">Chain-of-custody logged · 7-yr retention</Pill>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 'var(--sp-7)',
        }}
      >
        <TemplatePicker selected={selected} onSelect={setSelected} />

        {selected && (
          <div
            style={{
              padding: 'var(--sp-5)',
              background: 'var(--info-bg)',
              color: 'var(--info-text)',
              borderRadius: 'var(--r-md)',
              display: 'flex',
              gap: 'var(--sp-4)',
              alignItems: 'center',
            }}
          >
            <FileSearch size={18} />
            <span>
              Selected: <strong>{selected}</strong>
            </span>
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginLeft: 'auto' }}
              onClick={() => navigate(`/ras/run/${encodeURIComponent(selected)}`)}
            >
              Open runner →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const linkChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3) var(--sp-5)',
  background: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: 'var(--r-full)',
  fontSize: 12,
  color: 'var(--pcb-blue)',
  textDecoration: 'none',
};

export default function RAS(): JSX.Element {
  return (
    <Gate>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="run/:templateCode" element={<ReportRunner />} />
        <Route path="history" element={<ReportHistory />} />
        <Route path="sampling" element={<AicpaSampling />} />
      </Routes>
    </Gate>
  );
}
